import { getNetworkingApiBase } from '../components/networking/networkingApi';
import type { SlotRow } from '../components/networking/networkingApi';

export type AdminBookingRow = {
  id: number;
  table_code?: string;
  slot_start: string;
  slot_end: string;
  user_id: number | null;
  partner_type: string;
  manual_name?: string | null;
  manual_email?: string | null;
  manual_company?: string | null;
  manual_contacts_json?: string | null;
  guest_first_name: string;
  guest_last_name: string;
  guest_email: string;
  guest_phone: string;
  guest_company: string;
  guest_organization_type: string;
  created_at?: string;
};

export type ManualContactInput = {
  fullName: string;
  email?: string;
  phone?: string;
  organization: string;
};

export type AdminBookingPayload = {
  date: string;
  slotStart: string;
  partnerType: 'manual' | 'none';
  guestFirstName: string;
  guestLastName: string;
  guestEmail: string;
  guestPhone: string;
  guestCompany: string;
  guestOrganizationType?: string;
  manualContacts?: ManualContactInput[];
};

async function adminRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getNetworkingApiBase();
  const pathPart = path.startsWith('/') ? path : `/${path}`;
  const url = base ? `${base}${pathPart}` : `/networking-api${pathPart}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || res.statusText);
  }
  return body as T;
}

export async function fetchAdminNetworkingConfig() {
  const base = getNetworkingApiBase();
  const url = base ? `${base}/api/config` : '/networking-api/api/config';
  const res = await fetch(url, { credentials: 'include' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || res.statusText);
  return body as { success: boolean; data: { eventDates: string[]; slotCapacity: number } };
}

export async function fetchAdminNetworkingBookings(date?: string) {
  const q = date ? `?date=${encodeURIComponent(date)}` : '';
  return adminRequest<{ success: boolean; data: AdminBookingRow[] }>(
    `/api/admin/bookings${q}`
  );
}

export async function fetchAdminNetworkingSchedule(date: string) {
  return adminRequest<{ success: boolean; data: SlotRow[] }>(
    `/api/admin/bookings/schedule?date=${encodeURIComponent(date)}`
  );
}

export async function createAdminNetworkingBooking(payload: AdminBookingPayload) {
  return adminRequest<{ success: boolean; data: AdminBookingRow }>('/api/admin/bookings', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAdminNetworkingBooking(id: number, payload: Omit<AdminBookingPayload, 'date' | 'slotStart'>) {
  return adminRequest<{ success: boolean; data: AdminBookingRow }>(`/api/admin/bookings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function cancelAdminNetworkingBooking(id: number) {
  return adminRequest<{ success: boolean }>(`/api/admin/bookings/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
